import { clientPath } from "@/lib/record-paths";

// Mirror of the Rust `parse_resource_uri` / `ResourceKind`
// (`pilot-transports/src/http/mcp/resources.rs`). Only kinds with a
// standalone SPA route are listed — a kind absent from this table resolves
// to `null` rather than a dead link.
const ROUTE_BY_KIND: Record<string, (id: string) => string> = {
  deal: (id) => `/deals/${id}`,
  customer: (id) => clientPath(id),
  // A contact has no standalone page; its ref id is its customer's id.
  contact: (id) => clientPath(id),
  // Autonomy run → the Activity tab, focused on this run via the ?run=
  // query param (read by `MaxwellActivity`).
  agent_run: (id) => `/maxwell/activity?run=${id}`,
  // Deliberately no "rfq" case: the RFQ detail route is
  // `/energy-advisor/:customerId/rfqs/:rfqId` and needs a customer id that a
  // bare `powerup://rfq/<id>` resource URI never carries — building
  // `/rfqs/${id}` here would be a dead link. Same reasoning as
  // `notification-href.ts`. Commission and proposal resource kinds are
  // likewise omitted: neither has a standalone SPA route to link to.
};

// `powerup://` is the scheme the server builds. Messages saved before the
// product rename hold `northstar://` URIs, so those still resolve.
const RESOURCE_SCHEMES = ["powerup://", "northstar://"] as const;

/**
 * Resolve an in-app `powerup://<kind>/<uuid>` resource URI (the scheme
 * Maxwell emits for `entity_refs` chips and tool-result `status_resource`
 * fields) to a SPA route, or `null` if the kind isn't navigable.
 */
export function resolveResourceUri(uri: string): string | null {
  const scheme = RESOURCE_SCHEMES.find((s) => uri.startsWith(s));
  if (!scheme) return null;
  const [kind, id] = uri.slice(scheme.length).split("/");
  if (!kind || !id) return null;
  return ROUTE_BY_KIND[kind]?.(id) ?? null;
}
